'use client';

import { c as _c } from "react/compiler-runtime";
import { type CandlePoint, type ChartMarker, type CrosshairStyle, type DegenOptions, type HoverPoint, type LineCurve, Liveline, type LivelineFrameSource, type LivelinePoint, type LivelineProps, type LivelineSeries, type OrderbookData, type OrderbookEntry, type RangeBounds, type ReferenceLine, type SeriesEndpointMarker, type TimeHorizon, type TradeTapeEvent } from '@morfi/chart';
import { useLocale, useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { buildPerSeriesTooltip, liveChartAxisOptions, liveChartLegendStyle } from '@/components/chart/liveline-crosshair-style';
import { type OutcomeColorClasses, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { extendChartToLeftEdge } from '@/lib/chart/chart-window-boundaries';
import { buildCrosshairTimeRange, buildLiveCrosshairTimeRange } from '@/lib/chart/crosshair-time-range';
import { consensusDecimals, formatPercent, formatPercentTrim } from '@/lib/chart/format';
import { partitionCandlesByTime } from '@/lib/chart/map-market-chart-rows';
import { outcomeHueToStepVar } from '@/lib/chart/outcome-hue';
import { buildStrokeByIndex } from '@/lib/chart/series-color';
import { buildTargetRevealExtender, createTargetReveal, type TargetReveal } from '@/lib/chart/target-reveal';
import { formatTimeAxisLabel } from '@/lib/chart/time-axis-label';
import type { YAxisProperty } from '@/lib/chart/types';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import { useChartSettingsStore } from '@/stores/chart-settings';
export interface MarketChartOutcome {
  index: number;
  title: string;
  /** Latest probability / share (0-1). */
  twar: number;
  /** Latest price in USDC. */
  price: number;
  /** Latest market cap in USDC. */
  marketCap: number;
}
export interface MarketChartData {
  /** Candle geometry travels with its retained history frame. */
  readonly bucketSeconds?: number;
  line: ReadonlyArray<LivelinePoint>;
  readonly lineTimeKind?: 'sample';
  /** Value immediately before the requested window. */
  leftEdgeValue?: number;
  /** Authoritative launch state supplied only by full-lifetime history. */
  initialPoint?: LivelinePoint;
  ohlcv?: ReadonlyArray<CandlePoint>;
}
export interface MarketChartTrade {
  id: string;
  time: number;
  outcomeIndex: number;
  side: 'buy' | 'sell';
  quoteAmount: number;
}
export interface MarketChartBoost {
  id: string;
  time: number;
  quoteAmount: number;
}
export type MarketChartTradeTapeEvent = TradeTapeEvent;
export interface MarketChartProps {
  outcomes: ReadonlyArray<MarketChartOutcome>;
  chartData: Readonly<Record<number, MarketChartData>>;
  loading?: boolean;
  seriesOpacity?: number;
  liveValues?: Readonly<Record<number, number>>;
  /** Renderer-owned canonical clock and geometry for authoritative live Market Share. */
  frameSource?: LivelineFrameSource;
  threshold?: number;
  /** Exact threshold history supplied by the SDK policy adapter. */
  thresholdSeries?: ReadonlyArray<LivelinePoint>;
  /** Ease the Market Share axis out to `threshold` while hovering / pressing; defaults to the chart setting. */
  revealTargetOnHover?: boolean;
  /** Keeps resolved consensus history fully visible while tooltips still scrub. */
  locked?: boolean;
  /** Draws the Market Share resolution marker at this unix timestamp. */
  resolutionTimestamp?: number;
  /** Actual market resolution instant on a winner's Price or Market Cap chart. */
  resolvedAtTimestamp?: number;
  resolvedAtLabel?: string;
  /** Per-outcome Market Share projection revealed between now and the resolution marker. */
  resolutionProjection?: Readonly<Record<number, ReadonlyArray<LivelinePoint>>>;
  /** Caller-defined markers rendered at projected Market Share endpoints. */
  resolutionEndpointMarkers?: Readonly<Record<number, SeriesEndpointMarker>>;
  /** Reveals the future resolution horizon while hovering or holding the chart. */
  revealResolutionOnHover?: boolean;
  /** Caller-localized label for the resolution-horizon countdown. */
  resolutionLabel?: string;
  chartModeOverride?: 'consensus' | 'live';
  yAxisPropertyOverride?: YAxisProperty;
  /** Freezes a historical chart at this unix timestamp. */
  nowOverride?: number;
  /** Active All history keeps launch visible while its right edge advances. */
  historyStartTime?: number;
  candleNowSeconds?: number;
  mode?: 'line' | 'candle';
  focusedOutcomeIndex?: number;
  onSelectOutcome?: (index: number) => void;
  hiddenOutcomeIndices?: ReadonlyArray<number>;
  selectedOutcomeIndices?: ReadonlyArray<number>;
  onOutcomeVisibilityChange?: (index: number, visible: boolean) => void;
  candleWidthSeconds?: number;
  theme?: 'light' | 'dark';
  height?: number;
  mobileHeight?: number;
  windowSecs: number;
  /** Source-data window when it differs from the animated viewport target. */
  dataWindowSecs?: number;
  onWindowTransitionComplete?: (windowSecs: number) => void;
  onPlotWidthChange?: (width: number) => void;
  snapKey?: string | number;
  dotsBackground?: boolean;
  /** Enables endpoint pulses; candle charts also require a live candle. Defaults to true. */
  pulse?: boolean;
  degen?: boolean | DegenOptions;
  showBreakEven?: boolean;
  breakevens?: ReadonlyArray<{
    outcomeIndex: number;
    priceUsdc: number;
    marketCapUsdc?: number;
  }>;
  trades?: ReadonlyArray<MarketChartTrade>;
  boostEvents?: ReadonlyArray<MarketChartBoost>;
  /** Realtime trades rendered once as outcome-colored labels from the bottom-left. */
  tradeTape?: ReadonlyArray<MarketChartTradeTapeEvent>;
  /** Extend sparse history from the requested window's exact predecessor value. */
  extendLeftEdge?: boolean;
  /** Hold execution metrics at the last recorded value; conviction continues projecting. */
  holdLastValueToRightEdge?: boolean;
  seriesColorVar?: string;
  lineCurve?: LineCurve;
  /** Optional caller-owned hysteresis for execution-metric Y bounds. */
  axisRangeExtender?: (bounds: RangeBounds) => RangeBounds;
  className?: string;
}
const ENDPOINT_DOT_SIZE = 4.5;
const ENDPOINT_PULSE_SIZE = 14;
const MAX_TRADE_TAPE_SEEN_IDS = 5_000;
interface TradeTapeSeenStore {
  getIds: () => ReadonlyArray<string>;
  add: (tradeTape: ReadonlyArray<MarketChartTradeTapeEvent> | undefined) => void;
}
function appendTradeTapeSeenIds(seenIds: ReadonlyArray<string>, tradeTape: ReadonlyArray<MarketChartTradeTapeEvent> | undefined): ReadonlyArray<string> {
  const incomingIds = new Set((tradeTape ?? []).map(event => event.id));
  const seenIdSet = new Set(seenIds);
  const additions = [...incomingIds].filter(id => !seenIdSet.has(id));
  if (additions.length === 0) return seenIds;
  return [...seenIds, ...additions].slice(-MAX_TRADE_TAPE_SEEN_IDS);
}
function createTradeTapeSeenStore(initialTradeTape: ReadonlyArray<MarketChartTradeTapeEvent> | undefined): TradeTapeSeenStore {
  let seenIds = appendTradeTapeSeenIds([], initialTradeTape);
  return {
    getIds: () => seenIds,
    add: (tradeTape: ReadonlyArray<MarketChartTradeTapeEvent> | undefined) => {
      seenIds = appendTradeTapeSeenIds(seenIds, tradeTape);
    }
  };
}
function useTradeTapeSeenStore(tradeTape) {
  const $ = _c(6);
  let t0;
  if ($[0] !== tradeTape) {
    t0 = () => createTradeTapeSeenStore(tradeTape);
    $[0] = tradeTape;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [seenStore] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== seenStore || $[3] !== tradeTape) {
    t1 = () => {
      seenStore.add(tradeTape);
    };
    t2 = [seenStore, tradeTape];
    $[2] = seenStore;
    $[3] = tradeTape;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return seenStore;
}
function resolveTradeTape(events: ReadonlyArray<MarketChartTradeTapeEvent> | undefined): OrderbookData | undefined {
  if (events === undefined || events.length === 0) return undefined;
  const toEntry = (event: MarketChartTradeTapeEvent): OrderbookEntry => ({
    size: event.size,
    id: event.id,
    ...(event.time === undefined ? {} : {
      at: event.time
    }),
    ...(event.label !== undefined ? {
      label: event.label
    } : {}),
    ...(event.color !== undefined ? {
      color: event.color
    } : {})
  });
  return {
    bids: events.flatMap(event => event.side === 'buy' ? [toEntry(event)] : []),
    asks: events.flatMap(event => event.side === 'sell' ? [toEntry(event)] : [])
  };
}
interface MarketChartModel {
  theme: 'light' | 'dark';
  series: LivelineSeries[];
  candleData: {
    points: LivelinePoint[];
    value: number;
    color: string;
    candles: CandlePoint[];
    candleWidth: number;
    liveCandle?: CandlePoint;
  } | null;
  referenceLine?: ReferenceLine;
  referenceLines: ReferenceLine[];
  /** Eases the Market Share axis out to the off-axis target while revealed. */
  extendRange?: LivelineProps['extendRange'];
  /** Drives the hover / press target reveal. */
  onHover?: (point: HoverPoint | null) => void;
  timeHorizon?: TimeHorizon;
  markers: ChartMarker[];
  formatValue: (value: number, fractionDigits?: number, tickStep?: number) => string;
  formatTime: (time: number, windowSecs?: number) => string;
  showXAxis: boolean;
  showYAxis: boolean;
  showPriceLines: boolean;
  alwaysShowTooltip: boolean;
  crosshairStyle: CrosshairStyle;
  timeOffset: number;
}
function resolveCssVar(input: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const match = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(input);
  if (match === null) return input;
  const variableName = match[1];
  if (variableName === undefined) return fallback;
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  if (resolved.length > 0) return resolved;
  return match[2]?.trim() ?? fallback;
}
function latestPositiveValue(points: ReadonlyArray<LivelinePoint>): number | undefined {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = points[index]?.value;
    if (value !== undefined && Number.isFinite(value) && value > 0) return value;
  }
  return undefined;
}
function shiftPoint(point: LivelinePoint, offset: number): LivelinePoint {
  return {
    time: point.time + offset,
    value: point.value
  };
}
interface MarketChartBuildArgs {
  readonly props: MarketChartProps;
  readonly locale: string;
  readonly mountedAt: number;
  readonly resolvedTheme: string | undefined;
  readonly chartMode: 'consensus' | 'live';
  readonly yAxisProperty: YAxisProperty;
  readonly targetReveal: TargetReveal;
  readonly revealTargetOnHover: boolean;
  readonly showXAxis: boolean;
  readonly showYAxis: boolean;
  readonly showPriceLines: boolean;
  readonly alwaysShowTooltip: boolean;
  readonly showTrades: boolean;
  readonly showBoosts: boolean;
  readonly outcomeColors: ReadonlyArray<OutcomeColorClasses>;
  readonly formatters: ReturnType<typeof useFormattedCurrency>;
  readonly targetBadgeLabel: string;
  readonly breakEvenLabel: string;
}
function useMarketChartModel(props) {
  const $ = _c(26);
  const locale = useLocale();
  const t = useTranslations("chart");
  const {
    resolvedTheme
  } = useTheme();
  const [mountedAt] = useState(_temp);
  let t0;
  if ($[0] !== props.chartModeOverride) {
    t0 = state => props.chartModeOverride ?? state.chartMode;
    $[0] = props.chartModeOverride;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const chartMode = useChartSettingsStore(t0);
  let t1;
  if ($[2] !== props.yAxisPropertyOverride) {
    t1 = state_0 => props.yAxisPropertyOverride ?? state_0.yAxisProperty;
    $[2] = props.yAxisPropertyOverride;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const yAxisProperty = useChartSettingsStore(t1);
  const [targetReveal] = useState(_temp2);
  let t2;
  if ($[4] !== props.revealTargetOnHover) {
    t2 = state_1 => props.revealTargetOnHover ?? state_1.scaleMarketShareOnHover;
    $[4] = props.revealTargetOnHover;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const revealTargetOnHover = useChartSettingsStore(t2);
  const showXAxis = useChartSettingsStore(_temp3);
  const showYAxis = useChartSettingsStore(_temp4);
  const showPriceLines = useChartSettingsStore(_temp5);
  const alwaysShowTooltip = useChartSettingsStore(_temp6);
  const showTrades = useChartSettingsStore(_temp7);
  const showBoosts = useChartSettingsStore(_temp8);
  const formatters = useFormattedCurrency();
  let t3;
  if ($[6] !== props.outcomes) {
    t3 = props.outcomes.map(_temp9);
    $[6] = props.outcomes;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const outcomeColors = useOutcomeColor(t3);
  let t4;
  if ($[8] !== alwaysShowTooltip || $[9] !== chartMode || $[10] !== formatters || $[11] !== locale || $[12] !== mountedAt || $[13] !== outcomeColors || $[14] !== props || $[15] !== resolvedTheme || $[16] !== revealTargetOnHover || $[17] !== showBoosts || $[18] !== showPriceLines || $[19] !== showTrades || $[20] !== showXAxis || $[21] !== showYAxis || $[22] !== t || $[23] !== targetReveal || $[24] !== yAxisProperty) {
    t4 = buildMarketChartModel({
      props,
      locale,
      mountedAt,
      resolvedTheme,
      chartMode,
      yAxisProperty,
      targetReveal,
      revealTargetOnHover,
      showXAxis,
      showYAxis,
      showPriceLines,
      alwaysShowTooltip,
      showTrades,
      showBoosts,
      outcomeColors,
      formatters,
      targetBadgeLabel: t("target_badge_label"),
      breakEvenLabel: t("setting_break_even")
    });
    $[8] = alwaysShowTooltip;
    $[9] = chartMode;
    $[10] = formatters;
    $[11] = locale;
    $[12] = mountedAt;
    $[13] = outcomeColors;
    $[14] = props;
    $[15] = resolvedTheme;
    $[16] = revealTargetOnHover;
    $[17] = showBoosts;
    $[18] = showPriceLines;
    $[19] = showTrades;
    $[20] = showXAxis;
    $[21] = showYAxis;
    $[22] = t;
    $[23] = targetReveal;
    $[24] = yAxisProperty;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  return t4;
}
function _temp9(outcome) {
  return {
    title: outcome.title,
    index: outcome.index
  };
}
function _temp8(state_7) {
  return state_7.showBoosts;
}
function _temp7(state_6) {
  return state_6.showTrades;
}
function _temp6(state_5) {
  return state_5.alwaysShowTooltip;
}
function _temp5(state_4) {
  return state_4.showPriceLines;
}
function _temp4(state_3) {
  return state_3.showYAxis;
}
function _temp3(state_2) {
  return state_2.showXAxis;
}
function _temp2() {
  return createTargetReveal();
}
function _temp() {
  return Math.floor(Date.now() / 1000);
}
function buildMarketChartModel(args: MarketChartBuildArgs): MarketChartModel {
  const {
    props,
    locale,
    mountedAt,
    resolvedTheme,
    chartMode,
    yAxisProperty,
    targetReveal,
    revealTargetOnHover,
    showXAxis,
    showYAxis,
    showPriceLines,
    alwaysShowTooltip,
    showTrades,
    showBoosts,
    outcomeColors,
    formatters: {
      formatBalance,
      formatMarketCapAxis,
      formatPrice,
      formatPriceFixed
    },
    targetBadgeLabel,
    breakEvenLabel
  } = args;
  const {
    outcomes,
    chartData,
    liveValues,
    threshold: currentThreshold,
    thresholdSeries,
    locked = false,
    resolutionTimestamp,
    resolvedAtTimestamp,
    resolvedAtLabel,
    resolutionProjection,
    resolutionEndpointMarkers,
    revealResolutionOnHover = false,
    resolutionLabel,
    nowOverride,
    candleNowSeconds,
    mode = 'line',
    focusedOutcomeIndex,
    hiddenOutcomeIndices = [],
    candleWidthSeconds = 60,
    theme: themeOverride,
    windowSecs,
    dataWindowSecs = windowSecs,
    extendLeftEdge = false,
    holdLastValueToRightEdge = false,
    seriesColorVar,
    showBreakEven = false,
    breakevens = [],
    trades = [],
    boostEvents = []
  } = props;
  const threshold = currentThreshold ?? thresholdSeries?.at(-1)?.value;
  const theme: 'light' | 'dark' = themeOverride ?? (resolvedTheme === 'light' ? 'light' : 'dark');
  const showMarketShare = chartMode === 'consensus';
  const timeOffset = nowOverride === undefined ? 0 : mountedAt - nowOverride;
  const leftEdgeTime = (nowOverride ?? mountedAt) - dataWindowSecs;
  const uniformStroke = seriesColorVar === undefined ? undefined : resolveCssVar(seriesColorVar, '#3b82f6');
  const paletteColors = outcomes.map((_, index) => {
    const color = outcomeColors[index];
    return color === undefined ? undefined : resolveCssVar(outcomeHueToStepVar(color.hue, 400), '#3b82f6');
  });
  const strokeByIndex = buildStrokeByIndex(outcomes, paletteColors, uniformStroke);
  const hiddenOutcomeSet = new Set(hiddenOutcomeIndices);
  const currentValue = (outcome: MarketChartOutcome): number => {
    const liveValue = liveValues?.[outcome.index];
    if (liveValue !== undefined && Number.isFinite(liveValue)) return liveValue;
    if (showMarketShare) return outcome.twar;
    const entry = chartData[outcome.index];
    const points = entry?.line ?? [];
    const leftEdgeValue = entry?.leftEdgeValue;
    const terminalFallback = typeof leftEdgeValue === 'number' && Number.isFinite(leftEdgeValue) ? leftEdgeValue : undefined;
    if (yAxisProperty === 'marketCap') {
      return latestPositiveValue(points) ?? terminalFallback ?? outcome.marketCap;
    }
    return points.at(-1)?.value ?? terminalFallback ?? outcome.price;
  };
  const outcomeSeries = outcomes.toSorted((left, right) => currentValue(right) - currentValue(left)).flatMap<LivelineSeries>(outcome => {
    const entry = chartData[outcome.index];
    const outcomeCurrentValue = currentValue(outcome);
    const rawPoints = entry === undefined ? [] : extendLeftEdge ? extendChartToLeftEdge({
      points: entry.line,
      leftEdgeValue: entry.leftEdgeValue,
      leftEdgeTime,
      ...(entry.initialPoint === undefined ? {} : {
        initialPoint: entry.initialPoint
      })
    }) : [...entry.line];
    return [{
      id: `outcome-${outcome.index}`,
      label: outcome.title,
      color: strokeByIndex[outcome.index] ?? '#3b82f6',
      data: rawPoints.map(point => shiftPoint(point, timeOffset)),
      visible: !hiddenOutcomeSet.has(Number(outcome.index)),
      value: holdLastValueToRightEdge && !showMarketShare ? entry?.line.at(-1)?.value ?? rawPoints.at(-1)?.value ?? outcomeCurrentValue : outcomeCurrentValue
    }];
  });
  const plottedValues = outcomeSeries.flatMap(entry => [entry.value, ...entry.data.map(point => point.value)]);
  const plottedLow = Math.min(...plottedValues);
  const plottedHigh = Math.max(...plottedValues);
  const axisDecimals = showMarketShare && Number.isFinite(plottedLow) && Number.isFinite(plottedHigh) ? consensusDecimals(plottedHigh - plottedLow) : 1;
  const series = outcomeSeries;
  const formatValue = (value: number, fractionDigits?: number, tickStep?: number): string => {
    if (showMarketShare) return formatPercent(value, axisDecimals);
    if (yAxisProperty === 'marketCap') {
      return tickStep === undefined ? formatBalance(value, 'compactDecimal') : formatMarketCapAxis(value, tickStep);
    }
    if (fractionDigits !== undefined && (mode === 'candle' || Math.abs(value) < 1)) {
      return formatPriceFixed(value, fractionDigits);
    }
    return Math.abs(value) >= 1 ? formatBalance(value, 'compact') : formatPrice(value);
  };
  const formatTime = (time: number, renderedWindowSecs: number = windowSecs): string => formatTimeAxisLabel(time, renderedWindowSecs, locale, timeOffset);
  const formatTooltipValue = (value: number): string => {
    if (showMarketShare) return formatPercent(value, 2);
    if (yAxisProperty === 'marketCap') return formatBalance(value, 'compactDecimal');
    return formatPrice(value);
  };
  const formatCrosshairTimeRange = (fromSecs: number, toSecs: number): string => nowOverride === undefined ? buildLiveCrosshairTimeRange(fromSecs - timeOffset, toSecs - timeOffset, locale) : buildCrosshairTimeRange(fromSecs - timeOffset, toSecs - timeOffset, nowOverride, locale);
  const crosshairStyle = buildPerSeriesTooltip({
    formatSeriesValue: formatTooltipValue,
    formatTimeRange: formatCrosshairTimeRange,
    alwaysShow: alwaysShowTooltip
  });
  const chartBackgroundColor = resolveCssVar('var(--background-primary)', theme === 'light' ? '#ffffff' : '#0d0d0d');
  const referenceLine = showMarketShare && threshold !== undefined && !revealResolutionOnHover ? {
    value: threshold,
    ...(thresholdSeries === undefined || thresholdSeries.length === 0 ? {} : {
      points: thresholdSeries.map(point => shiftPoint(point, timeOffset))
    }),
    ...(revealTargetOnHover && !locked ? {
      pathReveal: targetReveal.progress
    } : {}),
    label: 'Consensus',
    style: 'dashed' as const,
    color: resolveCssVar('var(--feedback-warning-content)', '#f0b033'),
    labelColor: resolveCssVar('var(--feedback-warning-content)', '#f0b033'),
    labelFont: '500 11px system-ui, sans-serif',
    labelPosition: 'right-axis' as const,
    labelBadge: true,
    showValue: true,
    formatValue: (value: number): string => formatPercentTrim(value, 1, locale),
    dashPattern: [4, 4] as [number, number],
    excludeFromRange: true,
    offAxisBadge: true,
    offAxisBadgeLabel: targetBadgeLabel
  } : undefined;

  // Resolved history always fits the full consensus path; trading history
  // reveals it while scrubbing and eases back to the outcome range on leave.
  const marketShareRangeExtender = showMarketShare && threshold !== undefined && (locked || revealTargetOnHover) && !revealResolutionOnHover ? buildTargetRevealExtender(threshold, locked ? (): number => 1 : targetReveal.progress) : undefined;
  const extendRange = marketShareRangeExtender ?? props.axisRangeExtender;
  const onHover = locked || marketShareRangeExtender === undefined ? undefined : (point: HoverPoint | null): void => targetReveal.setActive(point !== null);
  const breakEvenLines = !showBreakEven || showMarketShare ? [] : breakevens.flatMap<ReferenceLine>(entry => {
    if (mode === 'candle' ? entry.outcomeIndex !== (focusedOutcomeIndex ?? outcomes[0]?.index) : hiddenOutcomeSet.has(entry.outcomeIndex)) return [];
    const value = yAxisProperty === 'marketCap' ? entry.marketCapUsdc : entry.priceUsdc;
    if (value === undefined || !Number.isFinite(value) || value <= 0) return [];
    return [{
      value,
      label: breakEvenLabel,
      style: 'dashed',
      color: strokeByIndex[entry.outcomeIndex] ?? '#3b82f6',
      labelColor: strokeByIndex[entry.outcomeIndex] ?? '#3b82f6',
      labelFont: '500 11px system-ui, sans-serif',
      labelPosition: 'left',
      showValue: true,
      formatValue: formatTooltipValue,
      labelBadge: true,
      dashPattern: [2, 4],
      excludeFromRange: true,
      offAxisBadge: true,
      offAxisBadgeLabel: breakEvenLabel
    }];
  });
  const referenceLines: ReferenceLine[] = [...breakEvenLines, ...(!showMarketShare && resolvedAtTimestamp !== undefined && resolvedAtLabel !== undefined ? [{
    at: resolvedAtTimestamp + timeOffset,
    label: resolvedAtLabel,
    color: resolveCssVar('var(--feedback-success-content)', '#26a269'),
    style: 'dashed' as const,
    width: 1.5,
    dashPattern: [3, 4] as [number, number]
  }] : []), ...(showMarketShare && resolutionTimestamp !== undefined && !revealResolutionOnHover ? [{
    at: resolutionTimestamp + timeOffset,
    color: resolveCssVar('var(--feedback-success-accent)', '#26a269'),
    style: 'dashed' as const,
    width: 1.5,
    dashPattern: [3, 4] as [number, number]
  }] : [])];
  const timeHorizon = showMarketShare && revealResolutionOnHover && resolutionTimestamp !== undefined && resolutionLabel !== undefined ? {
    at: resolutionTimestamp + timeOffset,
    label: resolutionLabel,
    color: resolveCssVar('var(--feedback-error-content)', '#f04444'),
    projectionOpacity: 0.6,
    ...(resolutionEndpointMarkers === undefined ? {} : {
      endpointMarkers: Object.fromEntries(Object.entries(resolutionEndpointMarkers).map(([index, marker]) => [`outcome-${index}`, {
        ...marker,
        fillColor: resolveCssVar(marker.fillColor, marker.fillColor),
        symbolColor: resolveCssVar(marker.symbolColor, marker.symbolColor)
      }]))
    }),
    ...(resolutionProjection === undefined ? {} : {
      series: Object.fromEntries(Object.entries(resolutionProjection).map(([index, points]) => [`outcome-${index}`, points.map(point => shiftPoint(point, timeOffset))]))
    })
  } : undefined;
  const markers: ChartMarker[] = [...(showTrades ? trades.map(trade => ({
    id: `trade:${trade.id}`,
    kind: 'trade' as const,
    time: trade.time + timeOffset,
    seriesId: `outcome-${trade.outcomeIndex}`,
    side: trade.side,
    color: trade.side === 'buy' ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
    backgroundColor: chartBackgroundColor,
    ...(trade.quoteAmount > 0 ? {
      label: String(trade.quoteAmount)
    } : {})
  })) : []), ...(showBoosts ? boostEvents.map(boost => ({
    id: `boost:${boost.id}`,
    kind: 'boost' as const,
    time: boost.time + timeOffset,
    color: resolveCssVar('var(--feedback-warning-content)', '#f0b033'),
    ...(boost.quoteAmount > 0 ? {
      label: String(boost.quoteAmount)
    } : {})
  })) : [])];
  const focusIndex = focusedOutcomeIndex ?? outcomes[0]?.index;
  const focusSeries = focusIndex === undefined ? undefined : series.find(entry => entry.id === `outcome-${focusIndex}`);
  const candleEntry = mode === 'candle' && focusIndex !== undefined ? chartData[focusIndex] : undefined;
  const candleData = (() => {
    if (mode !== 'candle' || focusSeries === undefined || candleEntry === undefined) {
      return null;
    }
    const source = candleEntry.ohlcv ?? [];
    const candleNow = candleNowSeconds ?? nowOverride ?? mountedAt;
    const {
      closed,
      live
    } = partitionCandlesByTime(source, candleWidthSeconds, candleNow);
    return {
      points: focusSeries.data,
      value: live?.close ?? focusSeries.value,
      color: focusSeries.color,
      candles: closed.map(candle => ({
        ...candle,
        time: candle.time + timeOffset
      })),
      candleWidth: candleWidthSeconds,
      ...(live === undefined ? {} : {
        liveCandle: {
          ...live,
          time: live.time + timeOffset
        }
      })
    };
  })();
  return {
    theme,
    series,
    candleData,
    ...(referenceLine === undefined ? {} : {
      referenceLine
    }),
    referenceLines,
    ...(extendRange === undefined ? {} : {
      extendRange
    }),
    ...(onHover === undefined ? {} : {
      onHover
    }),
    ...(timeHorizon === undefined ? {} : {
      timeHorizon
    }),
    markers,
    formatValue,
    formatTime,
    showXAxis,
    showYAxis,
    showPriceLines,
    alwaysShowTooltip,
    crosshairStyle,
    timeOffset
  };
}
export function MarketChart(allProps: MarketChartProps): React.ReactNode {
  const {
    height = 400,
    mobileHeight,
    loading = false,
    seriesOpacity = 1,
    snapKey,
    dotsBackground = false,
    degen,
    onSelectOutcome,
    onOutcomeVisibilityChange,
    className
  } = allProps;
  const model = useMarketChartModel(allProps);
  const tradeTapeSeenStore = useTradeTapeSeenStore(allProps.tradeTape);
  const tradeTapeOrderbook = resolveTradeTape(allProps.tradeTape);
  const chartKey = snapKey === undefined ? undefined : String(snapKey);
  const padding = {
    top: 12,
    right: 12,
    bottom: model.showXAxis ? 28 : 8,
    left: 0
  };
  return <div data-testid="market-chart-canvas-area" data-loading={loading} className={cn('h-[var(--chart-canvas-h)] overflow-hidden max-sm:h-[var(--chart-canvas-h-mobile)]', className)} style={{
    width: '100%',
    ['--chart-canvas-h' as string]: `${height}px`,
    ['--chart-canvas-h-mobile' as string]: `${mobileHeight ?? height}px`,
    position: 'relative',
    opacity: seriesOpacity
  }} data-hidden-outcomes={(allProps.hiddenOutcomeIndices ?? []).length} aria-busy={loading}>
      {dotsBackground ? <div aria-hidden className="chart-surface-underlay">
          <div className="chart-surface-dots absolute inset-0" />
        </div> : null}
      <MarketChartRenderer allProps={allProps} model={model} loading={loading} chartKey={chartKey} padding={padding} tradeTapeOrderbook={tradeTapeOrderbook} tradeTapeSeenStore={tradeTapeSeenStore} degen={degen} onSelectOutcome={onSelectOutcome} onOutcomeVisibilityChange={onOutcomeVisibilityChange} />
    </div>;
}
interface MarketChartRendererProps {
  readonly allProps: MarketChartProps;
  readonly model: MarketChartModel;
  readonly loading: boolean;
  readonly chartKey: string | undefined;
  readonly padding: {
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
    readonly left: number;
  };
  readonly tradeTapeOrderbook: OrderbookData | undefined;
  readonly tradeTapeSeenStore: TradeTapeSeenStore;
  readonly degen: boolean | DegenOptions | undefined;
  readonly onSelectOutcome: ((index: number) => void) | undefined;
  readonly onOutcomeVisibilityChange: ((index: number, visible: boolean) => void) | undefined;
}
function MarketChartRenderer(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = props.model.candleData === null ? <LineMarketChart {...props} /> : <CandleMarketChart {...props} candleData={props.model.candleData} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function CandleOutcomeSelector(t0) {
  const $ = _c(10);
  const {
    series,
    focusedOutcomeIndex,
    onSelectOutcome
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <legend className="sr-only">Chart outcomes</legend>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== focusedOutcomeIndex || $[2] !== onSelectOutcome || $[3] !== series) {
    let t3;
    if ($[5] !== focusedOutcomeIndex || $[6] !== onSelectOutcome) {
      t3 = entry => <button aria-pressed={entry.id === `outcome-${focusedOutcomeIndex}`} className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-control px-2 py-1.5 text-caption text-foreground-secondary" key={entry.id} onClick={() => {
        const outcomeIndex = Number(entry.id.replace("outcome-", ""));
        if (Number.isInteger(outcomeIndex)) {
          onSelectOutcome(outcomeIndex);
        }
      }} type="button"><span aria-hidden={true} className="size-2 rounded-full" style={{
          backgroundColor: entry.color
        }} /><span>{entry.label}</span></button>;
      $[5] = focusedOutcomeIndex;
      $[6] = onSelectOutcome;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = series.map(t3);
    $[1] = focusedOutcomeIndex;
    $[2] = onSelectOutcome;
    $[3] = series;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <fieldset className="relative z-10 flex min-w-0 shrink-0 items-center gap-1 overflow-x-auto">{t1}{t2}</fieldset>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function CandleMarketChart(t0) {
  const $ = _c(50);
  const {
    allProps,
    model,
    loading,
    chartKey,
    padding,
    tradeTapeOrderbook,
    tradeTapeSeenStore,
    degen,
    onSelectOutcome,
    candleData
  } = t0;
  let t1;
  if ($[0] !== allProps.focusedOutcomeIndex || $[1] !== model.series || $[2] !== onSelectOutcome) {
    t1 = onSelectOutcome === undefined ? null : <CandleOutcomeSelector series={model.series} focusedOutcomeIndex={allProps.focusedOutcomeIndex} onSelectOutcome={onSelectOutcome} />;
    $[0] = allProps.focusedOutcomeIndex;
    $[1] = model.series;
    $[2] = onSelectOutcome;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== allProps.lineCurve) {
    t2 = allProps.lineCurve === undefined ? {} : {
      curve: allProps.lineCurve
    };
    $[4] = allProps.lineCurve;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== allProps.nowOverride || $[7] !== model.timeOffset) {
    t3 = allProps.nowOverride === undefined ? {} : {
      nowSeconds: allProps.nowOverride + model.timeOffset
    };
    $[6] = allProps.nowOverride;
    $[7] = model.timeOffset;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== allProps.onWindowTransitionComplete) {
    t4 = allProps.onWindowTransitionComplete === undefined ? {} : {
      onWindowTransitionComplete: allProps.onWindowTransitionComplete
    };
    $[9] = allProps.onWindowTransitionComplete;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== allProps.onPlotWidthChange) {
    t5 = allProps.onPlotWidthChange === undefined ? {} : {
      onPlotWidthChange: allProps.onPlotWidthChange
    };
    $[11] = allProps.onPlotWidthChange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = allProps.pulse !== false && candleData.liveCandle !== undefined;
  let t7;
  if ($[13] !== tradeTapeOrderbook || $[14] !== tradeTapeSeenStore) {
    t7 = tradeTapeOrderbook === undefined ? {} : {
      orderbook: tradeTapeOrderbook,
      getOrderbookInitialSeenIds: tradeTapeSeenStore.getIds
    };
    $[13] = tradeTapeOrderbook;
    $[14] = tradeTapeSeenStore;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== degen) {
    t8 = degen === undefined ? {} : {
      degen
    };
    $[16] = degen;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== candleData.liveCandle) {
    t9 = candleData.liveCandle === undefined ? {} : {
      liveCandle: candleData.liveCandle
    };
    $[18] = candleData.liveCandle;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = model.showXAxis ? model.formatTime : _temp10;
  let t11;
  if ($[20] !== model.showYAxis) {
    t11 = model.showYAxis ? {
      axisOptions: liveChartAxisOptions
    } : {};
    $[20] = model.showYAxis;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== allProps.windowSecs || $[23] !== candleData.candleWidth || $[24] !== candleData.candles || $[25] !== candleData.color || $[26] !== candleData.points || $[27] !== candleData.value || $[28] !== chartKey || $[29] !== loading || $[30] !== model.formatValue || $[31] !== model.referenceLines || $[32] !== model.showPriceLines || $[33] !== model.showYAxis || $[34] !== model.theme || $[35] !== padding || $[36] !== t10 || $[37] !== t11 || $[38] !== t2 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t12 = <Liveline key={chartKey} data={candleData.points} value={candleData.value} color={candleData.color} theme={model.theme} {...t2} window={allProps.windowSecs} {...t3} {...t4} {...t5} grid={model.showYAxis} badge={false} scrub={true} valueLine={model.showPriceLines} useSeriesColorForDashLines={true} fill={true} pulse={t6} loading={loading} {...t7} momentum={false} edgeFade={false} {...t8} mode="candle" candles={candleData.candles} candleWidth={candleData.candleWidth} {...t9} referenceLines={model.referenceLines} formatValue={model.formatValue} formatTime={t10} padding={padding} {...t11} className="block h-full w-full" />;
    $[22] = allProps.windowSecs;
    $[23] = candleData.candleWidth;
    $[24] = candleData.candles;
    $[25] = candleData.color;
    $[26] = candleData.points;
    $[27] = candleData.value;
    $[28] = chartKey;
    $[29] = loading;
    $[30] = model.formatValue;
    $[31] = model.referenceLines;
    $[32] = model.showPriceLines;
    $[33] = model.showYAxis;
    $[34] = model.theme;
    $[35] = padding;
    $[36] = t10;
    $[37] = t11;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t1 || $[48] !== t12) {
    t13 = <div className="flex h-full min-h-0 flex-col">{t1}{t12}</div>;
    $[47] = t1;
    $[48] = t12;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  return t13;
}
function _temp10() {
  return "";
}
function LineMarketChart(t0) {
  const $ = _c(68);
  const {
    allProps,
    model,
    loading,
    chartKey,
    padding,
    tradeTapeOrderbook,
    tradeTapeSeenStore,
    degen,
    onOutcomeVisibilityChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== allProps.lineCurve) {
    t2 = allProps.lineCurve === undefined ? {} : {
      curve: allProps.lineCurve
    };
    $[1] = allProps.lineCurve;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== allProps.frameSource) {
    t3 = allProps.frameSource === undefined ? {} : {
      frameSource: allProps.frameSource
    };
    $[3] = allProps.frameSource;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== allProps.historyStartTime || $[6] !== model.timeOffset) {
    t4 = allProps.historyStartTime === undefined ? {} : {
      historyStartTime: allProps.historyStartTime + model.timeOffset
    };
    $[5] = allProps.historyStartTime;
    $[6] = model.timeOffset;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== allProps.nowOverride || $[9] !== model.timeOffset) {
    t5 = allProps.nowOverride === undefined ? {} : {
      nowSeconds: allProps.nowOverride + model.timeOffset
    };
    $[8] = allProps.nowOverride;
    $[9] = model.timeOffset;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== allProps.onWindowTransitionComplete) {
    t6 = allProps.onWindowTransitionComplete === undefined ? {} : {
      onWindowTransitionComplete: allProps.onWindowTransitionComplete
    };
    $[11] = allProps.onWindowTransitionComplete;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== allProps.onPlotWidthChange) {
    t7 = allProps.onPlotWidthChange === undefined ? {} : {
      onPlotWidthChange: allProps.onPlotWidthChange
    };
    $[13] = allProps.onPlotWidthChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = allProps.pulse !== false;
  let t9;
  if ($[15] !== tradeTapeOrderbook || $[16] !== tradeTapeSeenStore) {
    t9 = tradeTapeOrderbook === undefined ? {} : {
      orderbook: tradeTapeOrderbook,
      getOrderbookInitialSeenIds: tradeTapeSeenStore.getIds
    };
    $[15] = tradeTapeOrderbook;
    $[16] = tradeTapeSeenStore;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== degen) {
    t10 = degen === undefined ? {} : {
      degen
    };
    $[18] = degen;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== model.referenceLine) {
    t11 = model.referenceLine === undefined ? {} : {
      referenceLine: model.referenceLine
    };
    $[20] = model.referenceLine;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== model.extendRange) {
    t12 = model.extendRange === undefined ? {} : {
      extendRange: model.extendRange
    };
    $[22] = model.extendRange;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== model.onHover) {
    t13 = model.onHover === undefined ? {} : {
      onHover: model.onHover
    };
    $[24] = model.onHover;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== model.timeHorizon) {
    t14 = model.timeHorizon === undefined ? {} : {
      timeHorizon: model.timeHorizon
    };
    $[26] = model.timeHorizon;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = model.showXAxis ? model.formatTime : _temp11;
  let t16;
  if ($[28] !== model.showYAxis) {
    t16 = model.showYAxis ? {
      axisOptions: liveChartAxisOptions
    } : {};
    $[28] = model.showYAxis;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== onOutcomeVisibilityChange) {
    t17 = (seriesId, visible) => {
      if (onOutcomeVisibilityChange === undefined) {
        return;
      }
      const outcomeIndex = Number(seriesId.replace("outcome-", ""));
      if (Number.isInteger(outcomeIndex)) {
        onOutcomeVisibilityChange(outcomeIndex, visible);
      }
    };
    $[30] = onOutcomeVisibilityChange;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== allProps.selectedOutcomeIndices) {
    t18 = allProps.selectedOutcomeIndices?.map(_temp12);
    $[32] = allProps.selectedOutcomeIndices;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== allProps) {
    t19 = seriesId_0 => {
      const outcomeIndex_0 = Number(seriesId_0.replace("outcome-", ""));
      if (Number.isInteger(outcomeIndex_0)) {
        allProps.onSelectOutcome?.(outcomeIndex_0);
      }
    };
    $[34] = allProps;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== allProps.windowSecs || $[37] !== chartKey || $[38] !== loading || $[39] !== model.alwaysShowTooltip || $[40] !== model.crosshairStyle || $[41] !== model.formatValue || $[42] !== model.markers || $[43] !== model.referenceLines || $[44] !== model.series || $[45] !== model.showPriceLines || $[46] !== model.showYAxis || $[47] !== model.theme || $[48] !== padding || $[49] !== t10 || $[50] !== t11 || $[51] !== t12 || $[52] !== t13 || $[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t17 || $[57] !== t18 || $[58] !== t19 || $[59] !== t2 || $[60] !== t3 || $[61] !== t4 || $[62] !== t5 || $[63] !== t6 || $[64] !== t7 || $[65] !== t8 || $[66] !== t9) {
    t20 = <Liveline key={chartKey} data={t1} value={0} series={model.series} theme={model.theme} {...t2} window={allProps.windowSecs} {...t3} {...t4} {...t5} {...t6} {...t7} grid={model.showYAxis} badge={false} scrub={true} alwaysShowTooltip={model.alwaysShowTooltip} crosshairStyle={model.crosshairStyle} valueLine={model.showPriceLines} useSeriesColorForDashLines={true} fill={false} pulse={t8} dotSize={ENDPOINT_DOT_SIZE} pulseSize={ENDPOINT_PULSE_SIZE} loading={loading} {...t9} momentum={false} edgeFade={false} {...t10} {...t11} referenceLines={model.referenceLines} {...t12} {...t13} {...t14} markers={model.markers} markerCondenseDistance={8} formatValue={model.formatValue} formatTime={t15} padding={padding} {...t16} onSeriesToggle={t17} selectedSeriesIds={t18} onSeriesSelect={t19} seriesToggleStyle={liveChartLegendStyle} className="block h-full w-full" />;
    $[36] = allProps.windowSecs;
    $[37] = chartKey;
    $[38] = loading;
    $[39] = model.alwaysShowTooltip;
    $[40] = model.crosshairStyle;
    $[41] = model.formatValue;
    $[42] = model.markers;
    $[43] = model.referenceLines;
    $[44] = model.series;
    $[45] = model.showPriceLines;
    $[46] = model.showYAxis;
    $[47] = model.theme;
    $[48] = padding;
    $[49] = t10;
    $[50] = t11;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
    $[59] = t2;
    $[60] = t3;
    $[61] = t4;
    $[62] = t5;
    $[63] = t6;
    $[64] = t7;
    $[65] = t8;
    $[66] = t9;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  return t20;
}
function _temp12(index) {
  return `outcome-${index}`;
}
function _temp11() {
  return "";
}